import * as React from 'react'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import { getIn } from 'formik'

import TextField from 'modules/core/components/TextField'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FieldArray, useFormContext } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import { EvaluationFormValues, buildEmptyQuestion } from '../useEvaluationFormForm'
import { useGetEvaluationForm } from '../useGetEvaluationForm'
import { QuestionItem } from './QuestionItem'

export const NEW_EVALUATION_FROM_SCRATCH = '-1'

interface Props {
  evaluationFormId?: number
  hasInvalidQuestions: boolean
}

export const EvaluationFormEditor = ({ evaluationFormId, hasInvalidQuestions }: Props) => {
  const form = useFormContext<EvaluationFormValues>()
  const fetchEvaluationFormId = evaluationFormId ?? form.values.basedOnId ?? null
  const { loading, evaluationForm } = useGetEvaluationForm(fetchEvaluationFormId)

  useEffect(() => {
    if (!loading && !!evaluationForm) {
      form.setFieldValue('name', form.values.duplicate ? '' : evaluationForm.name)
      form.setFieldValue('questions', evaluationForm.questions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEvaluationFormId, loading])

  if (loading) {
    return (
      <Box padding="s40" width="full" alignItems="center" flexGrow>
        <Spinner />
      </Box>
    )
  }

  return (
    <>
      <TextField
        mandatory
        label={i18n.t('ats.evaluation_forms.form.title_label')}
        value={form.values.name}
        onChange={(value) => form.setFieldValue('name', value)}
        error={form.errors.name}
      />
      <FieldArray
        name="questions"
        render={(arrayHelpers) => (
          <Box gap="s16">
            {form.values.questions.map((question, index) => {
              const fieldName = `questions.${index}`
              const errors = getIn(form.touched, fieldName) && getIn(form.errors, fieldName)
              const isLastQuestion = form.values.questions.length < 2

              return (
                <QuestionItem
                  key={`${question.id}-${question.text}`}
                  value={question}
                  onEdit={(newQuestion) => form.setFieldValue(fieldName, newQuestion, false)}
                  onDelete={() => {
                    if (isLastQuestion) {
                      form.setFieldValue(fieldName, {
                        id: question.id,
                        text: '',
                        description: '',
                      })
                    } else {
                      arrayHelpers.handleRemove(index)()
                    }
                  }}
                  errors={errors}
                  isLastQuestion={isLastQuestion}
                />
              )
            })}

            <Button
              onClick={() => arrayHelpers.push(buildEmptyQuestion())}
              type="secondary"
              disabled={hasInvalidQuestions}
              iconName="add"
            >
              {i18n.t('ats.evaluation_forms.form.add_skill')}
            </Button>
          </Box>
        )}
      />
    </>
  )
}
